// Pure helpers behind RoastChart. No React, no Recharts — so the shapes the
// chart draws from can be unit-tested against fixture data.

import { formatConfidence, formatTemp, formatTime } from "./format";
import type {
  DetectedMilestone,
  EffectiveSource,
  MilestoneType,
  TempSource,
  Timeseries,
} from "./types";

export interface ChartRow {
  t: number;
  bt: number | null;
  ibts: number | null;
  ror: number | null;
  power: number | null;
  fan: number | null;
  drum: number | null;
}

// One object per sample — the shape Recharts wants.
export function buildChartRows(ts: Timeseries): ChartRow[] {
  return ts.elapsed_time_s.map((t, i) => ({
    t,
    bt: ts.bean_temp[i] ?? null,
    ibts: ts.ibts_temp[i] ?? null,
    ror: ts.ror[i] ?? null,
    power: ts.power_setting[i] ?? null,
    fan: ts.fan_setting[i] ?? null,
    drum: ts.drum_setting[i] ?? null,
  }));
}

// Human label for where a milestone's effective value came from. The
// wording matters: a recipe stamp is a plan value, not an observation
// (issue #8), and the chart must not let it read as one.
export function sourceLabel(source: EffectiveSource | string): string {
  switch (source) {
    case "manual":
      return "manual mark";
    case "recipe_auto":
      return "recipe auto-stamp";
    case "estimated":
      return "estimated";
    case "corrected":
      return "corrected";
    case "annotated":
      return "typed observation";
    case "unknown":
      return "marker, provenance unknown";
    case "none":
      return "no value";
    default:
      return String(source).replace(/_/g, " ");
  }
}

export function milestoneName(type: MilestoneType | string): string {
  switch (type) {
    case "turning_point":
      return "turning point";
    case "first_crack":
      return "first crack";
    default:
      return String(type).replace(/_/g, " ");
  }
}

// Short form for the reference-line tag: "FC", "TP", "Y", "C", "D".
export function milestoneAbbrev(type: MilestoneType | string): string {
  switch (type) {
    case "charge":
      return "CH";
    case "turning_point":
      return "TP";
    case "yellow":
      return "Y";
    case "first_crack":
      return "FC";
    case "drop":
      return "DR";
    default:
      return String(type).slice(0, 2).toUpperCase();
  }
}

export interface ChartMilestone {
  type: MilestoneType;
  time_s: number;
  temp: number | null;
  temp_source: TempSource | null;
  source: EffectiveSource;
  confidence: number;
  // "first crack · recipe auto-stamp · 25%"
  label: string;
  // "FC · auto · 25%"
  short: string;
}

// Only milestones with an effective time can be drawn. The label carries
// type, provenance and confidence together so none of the three can be
// read without the others.
export function chartMilestones(ms: DetectedMilestone[]): ChartMilestone[] {
  const out: ChartMilestone[] = [];
  for (const m of ms) {
    if (m.effective_time_s == null) continue;
    const conf = formatConfidence(m.effective_confidence).label;
    out.push({
      type: m.milestone_type,
      time_s: m.effective_time_s,
      temp: m.effective_temp,
      temp_source: m.effective_temp_source,
      source: m.effective_source,
      confidence: m.effective_confidence,
      label: `${milestoneName(m.milestone_type)} · ${sourceLabel(m.effective_source)} · ${conf}`,
      short: `${milestoneAbbrev(m.milestone_type)} · ${shortSource(m.effective_source)} · ${conf}`,
    });
  }
  return out.sort((a, b) => a.time_s - b.time_s);
}

function shortSource(source: EffectiveSource): string {
  switch (source) {
    case "recipe_auto":
      return "auto";
    case "estimated":
      return "est";
    case "corrected":
      return "corr";
    case "annotated":
      return "noted";
    case "manual":
      return "manual";
    case "unknown":
      return "unknown";
    default:
      return source;
  }
}

export interface Controls {
  power: number | null;
  fan: number | null;
  drum: number | null;
}

// Control state in force at time `t`: the last non-null value at or before
// `t` per knob (step semantics — a knob keeps its setting until it moves).
// Rows must be time-ordered.
export function controlAt(rows: ChartRow[], t: number): Controls {
  const out: Controls = { power: null, fan: null, drum: null };
  for (const r of rows) {
    if (r.t > t) break;
    if (r.power != null) out.power = r.power;
    if (r.fan != null) out.fan = r.fan;
    if (r.drum != null) out.drum = r.drum;
  }
  return out;
}

// "P7 F3 D9" — dashes for knobs with no reading.
export function formatControls(c: Controls): string {
  return [
    c.power != null ? `P${c.power}` : "P–",
    c.fan != null ? `F${c.fan}` : "F–",
    c.drum != null ? `D${c.drum}` : "D–",
  ].join(" ");
}

// Plain-text version of the chart for screen readers and for anyone who
// would rather read numbers than a picture. Says what each series is,
// its range, and each milestone with provenance.
export function chartTextSummary(
  ts: Timeseries,
  milestones: ChartMilestone[],
  unit: "C" | "F",
): string {
  const parts: string[] = [];
  const n = ts.elapsed_time_s.length;
  if (n === 0) return "No samples in this window.";
  const t0 = ts.elapsed_time_s[0];
  const t1 = ts.elapsed_time_s[n - 1];
  parts.push(
    `${n} samples from ${formatTime(t0)} to ${formatTime(t1)}.`,
  );
  const bt = range(ts.bean_temp);
  if (bt) parts.push(`Bean probe (BT) ${formatTemp(bt[0], unit)} to ${formatTemp(bt[1], unit)}.`);
  const ibts = range(ts.ibts_temp);
  if (ibts) parts.push(`IBTS ${formatTemp(ibts[0], unit)} to ${formatTemp(ibts[1], unit)}.`);
  const ror = range(ts.ror);
  if (ror) {
    const probe = ts.ror_source ? ts.ror_source.toUpperCase() : "";
    parts.push(
      `Rate of rise${probe ? ` (${probe})` : ""} ${ror[0].toFixed(1)} to ${ror[1].toFixed(1)} °${unit}/min.`,
    );
  }
  const rows = buildChartRows(ts);
  const start = controlAt(rows, t0);
  const end = controlAt(rows, t1);
  parts.push(`Controls at start ${formatControls(start)}, at end ${formatControls(end)}.`);
  for (const m of milestones) {
    const temp =
      m.temp != null
        ? ` at ${formatTemp(m.temp, unit)}${m.temp_source ? ` ${m.temp_source.toUpperCase()}` : ""}`
        : "";
    parts.push(
      `${capitalize(milestoneName(m.type))} at ${formatTime(m.time_s)}${temp}, ${sourceLabel(m.source)}, confidence ${formatConfidence(m.confidence).label}.`,
    );
  }
  return parts.join(" ");
}

function range(xs: (number | null)[]): [number, number] | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const x of xs) {
    if (x == null || Number.isNaN(x)) continue;
    if (x < lo) lo = x;
    if (x > hi) hi = x;
  }
  return lo === Infinity ? null : [lo, hi];
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
